
import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { getKarmasAction, deleteKarmaAction } from "../../../store/actions/KarmaAction.js"
import swal from "sweetalert"
import Loader from "../Common/Loader.jsx"
import { handleSuccess, showErrorAlert } from "../../../services/ErrorHandlingService.js"

const Karma = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const karmas = useSelector((state) => state.karma?.karmas || [])
  const numRows = useSelector((state) => state.karma?.num_rows || 0)
  const [isLoading, setIsLoading] = useState(false)

  const [searchQueries, setSearchQueries] = useState({
    name: "",
    prerequisite: "",
    duration: "",
    charges: "",
    status: "",
  })

  const [sortField, setSortField] = useState("name")
  const [sortOrder, setSortOrder] = useState("asc")
  const [currentPage, setCurrentPage] = useState(0)
  const rowsPerPage = 10

  const [activeSearchField, setActiveSearchField] = useState("")
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("")

  const fieldToDbMap = {
    id: "karma_id",
    name: "karma_name",
    prerequisite: "karma_depends_on",
    duration: "karma_duration_in_days",
    charges: "karma_charges",
    status: "karma_status",
  }

  useEffect(() => {
    const fetchKarma = async () => {
      setIsLoading(true)
      try {
        await fetchKarmaFromServer("", "", fieldToDbMap[sortField] || sortField, sortOrder, true, 0)
      } catch (error) {
        showErrorAlert("Failed to load karma list: " + error.message)
      } finally {
        setIsLoading(false)
      }
    }
    fetchKarma()
  }, [dispatch])

  const fetchKarmaFromServer = async (
    search_by = "",
    search_keyword = "",
    sort_by = "",
    sort_order = "",
    pagination = false,
    pageid = 0,
  ) => {
    try {
      const dbSortBy = fieldToDbMap[sort_by] || sort_by
      const dbSearchBy = fieldToDbMap[search_by] || search_by
      await dispatch(getKarmasAction(pagination, pageid, dbSearchBy, search_keyword, sort_order, dbSortBy))
    } catch (err) {
      console.error("Failed to fetch karma list:", err)
    }
  }

  const handleSearch = (e, field) => {
    const keyword = e.target.value

    const clearedQueries = {
      name: "",
      prerequisite: "",
      duration: "",
      charges: "",
      status: "",
    }

    const updatedQueries = { ...clearedQueries, [field]: keyword }
    setSearchQueries(updatedQueries)
    setCurrentPage(0)

    const dbField = fieldToDbMap[field] || field
    setActiveSearchField(dbField)
    setActiveSearchKeyword(keyword)

    const hasSorting = sortField && sortOrder
    fetchKarmaFromServer(
      dbField,
      keyword,
      hasSorting ? fieldToDbMap[sortField] || sortField : "",
      hasSorting ? sortOrder : "",
      true,
      0,
    )
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    fetchKarmaFromServer(
      activeSearchField,
      activeSearchKeyword,
      fieldToDbMap[sortField] || sortField,
      sortOrder,
      true,
      page,
    )
  }

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc"
    const newSortDbField = fieldToDbMap[field] || field
    setSortField(field)
    setSortOrder(order)
    fetchKarmaFromServer(activeSearchField, activeSearchKeyword, newSortDbField, order, true, currentPage)
  }

  const handleEdit = (karma) => {
    navigate(`/karma/update-karma/${karma.karma_id ?? karma.id}`, {
      state: { karma },
    })
  }

  const handleDelete = (karma) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, this karma cannot be recovered!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        setIsLoading(true)
        dispatch(deleteKarmaAction(karma.karma_id ?? karma.id))
          .then(() => {
            handleSuccess("Karma deleted successfully")
            fetchKarmaFromServer(
              activeSearchField,
              activeSearchKeyword,
              fieldToDbMap[sortField] || sortField,
              sortOrder,
              true,
              currentPage,
            )
          })
          .catch((error) => {
            showErrorAlert("Failed to delete karma: " + error.message)
          })
          .finally(() => {
            setIsLoading(false)
          })
      }
    })
  }

  const paginatedData = karmas
  const totalPages = Math.ceil(numRows / rowsPerPage)

  if (isLoading) {
    return <Loader />
  }

  return (
    <>
      <div className="row mb-3">
        <div className="col-xl-12 text-end">
          <Link to="/add-karma" className="btn btn-primary btn-rounded">
            + Add Karma
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-striped mb-4">
                  <thead className="table-light">
                    <tr>
                      <th onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>
                        Name {sortField === "name" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.name}
                          onChange={(e) => handleSearch(e, "name")}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Search Name"
                        />
                      </th>
                      <th onClick={() => handleSort("prerequisite")} style={{ cursor: "pointer" }}>
                        Prerequisite {sortField === "prerequisite" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.prerequisite}
                          onChange={(e) => handleSearch(e, "prerequisite")}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Search Prerequisite"
                        />
                      </th>
                      <th onClick={() => handleSort("duration")} style={{ cursor: "pointer" }}>
                        Duration {sortField === "duration" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.duration}
                          onChange={(e) => handleSearch(e, "duration")}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Search Duration"
                        />
                      </th>
                      <th onClick={() => handleSort("charges")} style={{ cursor: "pointer" }}>
                        Charges {sortField === "charges" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.charges}
                          onChange={(e) => handleSearch(e, "charges")}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Search Charges"
                        />
                      </th>
                      <th onClick={() => handleSort("status")} style={{ cursor: "pointer" }}>
                        Status {sortField === "status" && (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchQueries.status}
                          onChange={(e) => handleSearch(e, "status")}
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Search Status"
                        />
                      </th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map((karma) => (
                      <tr key={karma.karma_id ?? karma.id}>
                        <td>{karma.karma_name}</td>
                        <td>{karma.karma_depends_on}</td>
                        <td>{karma.karma_duration_in_days}</td>
                        <td>{karma.karma_charges}</td>
                        <td>
                          <span
                            className={`badge px-3 py-1 rounded-pill text-white ${
                              karma.karma_status?.toLowerCase() === "active" ? "bg-success" : "bg-danger"
                            }`}
                          >
                            {karma.karma_status}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2 flex-nowrap">
                            <Link
                              to="#"
                              onClick={(e) => {
                                e.preventDefault()
                                handleEdit(karma)
                              }}
                              className="text-success me-2"
                              title="Edit Karma"
                            >
                              <i className="fa fa-pencil-alt fs-5" />
                            </Link>
                            <span
                              onClick={() => handleDelete(karma)}
                              className="text-danger"
                              style={{ cursor: "pointer" }}
                              title="Delete Karma"
                            >
                              <i className="fa fa-trash fs-5" />
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {paginatedData.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          No karma found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div
                className="pagination-wrapper"
                style={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "20px",
                  flexWrap: "wrap",
                  flexDirection: "column",
                }}
              >
                <div>
                  Showing {karmas?.length === 0 ? 0 : currentPage * rowsPerPage + 1} to{" "}
                  {Math.min((currentPage + 1) * rowsPerPage, numRows)} of {numRows} entries
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <button
                    onClick={() => handlePageChange(Math.max(currentPage - 1, 0))}
                    disabled={currentPage === 0}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                      color: "#000",
                      cursor: currentPage === 0 ? "not-allowed" : "pointer",
                      opacity: currentPage === 0 ? 0.5 : 1,
                    }}
                  >
                    &lt;
                  </button>
                  {(() => {
                    const pages = []
                    const maxPagesToShow = 5
                    const startPage = Math.max(0, Math.min(currentPage - 2, totalPages - maxPagesToShow))
                    const endPage = Math.min(startPage + maxPagesToShow, totalPages)

                    if (startPage > 0) {
                      pages.push(
                        <button
                          key={0}
                          onClick={() => handlePageChange(0)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: "pointer",
                          }}
                        >
                          1
                        </button>,
                      )
                      if (startPage > 1) {
                        pages.push(
                          <span key="dots1" style={{ padding: "6px 12px", userSelect: "none" }}>
                            ...
                          </span>,
                        )
                      }
                    }

                    for (let i = startPage; i < endPage; i++) {
                      const isActive = currentPage === i
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: isActive ? "#007bff" : "#fff",
                            color: isActive ? "#fff" : "#000",
                            cursor: "pointer",
                          }}
                        >
                          {i + 1}
                        </button>,
                      )
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="dots2" style={{ padding: "6px 12px", userSelect: "none" }}>
                            ...
                          </span>,
                        )
                      }
                      pages.push(
                        <button
                          key={totalPages - 1}
                          onClick={() => handlePageChange(totalPages - 1)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: "pointer",
                          }}
                        >
                          {totalPages}
                        </button>,
                      )
                    }

                    return pages
                  })()}
                  <button
                     onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages - 1))}
                    disabled={currentPage === totalPages - 1}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                      color: "#000",
                      cursor: currentPage === totalPages - 1 ? "not-allowed" : "pointer",
                      opacity: currentPage === totalPages - 1 ? 0.5 : 1,
                    }}
                  >
                    &gt;
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Karma
